import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import AppNavbar from '@/components/feature/AppNavbar';
import AppFooter from '@/components/feature/AppFooter';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.beauty';
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png';

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Kakobuy Finds Beauty',
      description: 'Discover the latest Kakobuy Spreadsheet, product finds, category guides and comparison tools. Explore safe shopping strategies and updated spreadsheet resources.',
      inLanguage: 'en',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${siteUrl}/?s={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'Organization',
      '@id': `${siteUrl}/#organization`,
      name: 'Kakobuy Finds Beauty',
      url: siteUrl,
      logo: logoUrl,
      description: 'The Ultimate Spreadsheet Resource Hub For Smart Shopping Research. Independent research platform providing updated shopping guides, product comparison database and safety education.',
      sameAs: [
        'https://kaspreadsheet.com',
      ],
    },
  ],
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={homeJsonLd} />
      <AppNavbar />
      <HeroSection />
      <CategoryGridSection />
      <AboutSection />
      <SEOArticlesSection />
      <ArticleCTASection />
      <AppFooter />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20toned%20fashion%20product%20photography%20with%20cream%20beige%20terracotta%20color%20palette%2C%20elegant%20sneakers%20shoes%20accessories%20handbags%20arranged%20on%20clean%20marble%20surface%2C%20soft%20natural%20morning%20light%20from%20large%20window%20casting%20gentle%20shadows%2C%20editorial%20lifestyle%20aesthetic%2C%20minimalist%20composition%20with%20organic%20textures%20cotton%20linen%20silk%20fabric%20swatches%20scattered%2C%20high%20end%20boutique%20store%20visual%20merchandising%20style%2C%20no%20people%20visible%2C%20warm%20neutral%20background%20with%20subtle%20golden%20hour%20glow&width=1600&height=900&seq=kakobuy-hero-v2&orientation=landscape"
          alt="Kakobuy Spreadsheet fashion finds and shopping inspiration"
          title="Kakobuy Spreadsheet - Best Finds & Shopping Resources"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>

      <div className="relative z-10 w-full max-w-4xl mx-auto text-center px-4 md:px-6 pt-20">
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5">
          Kakobuy Spreadsheet &mdash; Discover The Best Finds, Products &amp; Shopping Resources
        </h1>
        <p className="text-base md:text-lg text-background-50/85 max-w-2xl mx-auto mb-8 leading-relaxed">
          Explore thousands of organized product finds, shopping spreadsheets, category guides and comparison insights designed to help buyers research smarter.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Kakobuy Sheet
            <i className="ri-external-link-line text-lg"></i>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/90 hover:bg-background-50 text-foreground-900 rounded-full text-base font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-shopping-bag-line text-lg"></i>
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mt-10 text-background-50/80 text-xs md:text-sm">
          <span className="flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Updated Resources
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Independent Research
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Product Comparison
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line text-accent-400"></i> Beginner Friendly
          </span>
        </div>
      </div>
    </section>
  );
}

function CategoryGridSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Explore Product Categories
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Browse our curated spreadsheet categories and discover trending finds across fashion, accessories and more.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat, idx) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group relative bg-background-50 border border-background-200/80 rounded-2xl p-4 md:p-5 text-center transition-all duration-300 hover:border-primary-300/60 hover:bg-secondary-50/80 cursor-pointer"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 rounded-xl bg-secondary-100 flex items-center justify-center group-hover:bg-primary-100 transition-colors duration-300">
                <i className={`${getCategoryIcon(idx)} text-xl md:text-2xl text-foreground-600 group-hover:text-primary-500 transition-colors duration-300`}></i>
              </div>
              <span className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap">
                {cat.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function getCategoryIcon(idx: number): string {
  const icons = [
    'ri-shopping-bag-line',
    'ri-t-shirt-line',
    'ri-t-shirt-2-line',
    'ri-shirt-line',
    'ri-user-line',
    'ri-user-smile-line',
    'ri-stack-line',
    'ri-user-3-line',
    'ri-trophy-line',
    'ri-vip-diamond-line',
    'ri-handbag-line',
    'ri-link',
    'ri-drop-line',
    'ri-more-line',
  ];
  return icons[idx] || 'ri-store-line';
}

function AboutSection() {
  return (
    <section className="py-16 md:py-20 bg-secondary-50">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="w-full h-64 md:h-80 lg:h-full min-h-[300px] rounded-3xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20product%20flat%20lay%20photography%20with%20beige%20cream%20warm%20tones%2C%20carefully%20arranged%20sneakers%20handbags%20belts%20accessories%20on%20textured%20linen%20fabric%20surface%2C%20soft%20diffused%20window%20light%20from%20left%20side%20creating%20gentle%20shadows%2C%20editorial%20magazine%20style%2C%20minimalist%20aesthetic%20with%20organic%20composition%2C%20neutral%20warm%20color%20palette%2C%20high%20end%20curated%20product%20display%2C%20clean%20sophisticated%20visual%20merchandising&width=800&height=1000&seq=kakobuy-about-product&orientation=portrait"
                alt="Kakobuy Spreadsheet curated product categories"
                title="Kakobuy Spreadsheet - Curated Fashion Finds"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6">
              What Is Kakobuy Spreadsheet?
            </h2>

            <div className="space-y-5 text-foreground-700 leading-relaxed text-sm md:text-base">
              <p>
                The <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> is a powerful research tool designed for savvy online shoppers who want to discover, organize and compare the best product finds across multiple categories. Rather than spending hours scrolling through endless listings, a well-structured spreadsheet puts thousands of curated items at your fingertips &mdash; complete with pricing details, category tags and direct shopping links.
              </p>

              <p>
                At its core, the Kakobuy Spreadsheet system works by aggregating popular and trending products from global marketplaces into an easy-to-navigate format. Whether you are hunting for the latest sneaker drops, seasonal fashion staples, everyday accessories or niche lifestyle goods, the spreadsheet format allows you to filter, sort and compare products with remarkable efficiency.
              </p>

              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-7 my-6">
                <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-4">
                  Traditional Product Search vs Kakobuy Spreadsheet
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 pr-4 font-semibold text-foreground-800">Feature</th>
                        <th className="text-left py-3 pr-4 font-semibold text-foreground-800">Traditional Search</th>
                        <th className="text-left py-3 font-semibold text-primary-600">Kakobuy Spreadsheet</th>
                      </tr>
                    </thead>
                    <tbody className="text-foreground-600">
                      <tr className="border-b border-background-100">
                        <td className="py-3 pr-4 font-medium text-foreground-700">Research Time</td>
                        <td className="py-3 pr-4">Hours of scrolling</td>
                        <td className="py-3 text-primary-600 font-medium">Minutes to scan</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 pr-4 font-medium text-foreground-700">Product Discovery</td>
                        <td className="py-3 pr-4">Limited by algorithm</td>
                        <td className="py-3 text-primary-600 font-medium">Curated &amp; categorized</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 pr-4 font-medium text-foreground-700">Organization</td>
                        <td className="py-3 pr-4">Scattered bookmarks</td>
                        <td className="py-3 text-primary-600 font-medium">Structured categories</td>
                      </tr>
                      <tr className="border-b border-background-100">
                        <td className="py-3 pr-4 font-medium text-foreground-700">Price Comparison</td>
                        <td className="py-3 pr-4">Manual tab-switching</td>
                        <td className="py-3 text-primary-600 font-medium">Side-by-side view</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-medium text-foreground-700">Beginner Friendly</td>
                        <td className="py-3 pr-4">Requires experience</td>
                        <td className="py-3 text-primary-600 font-medium">Easy to navigate</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <p>
                Beyond simple product listings, the Kakobuy Spreadsheet ecosystem also serves as an educational resource for shoppers who are new to international marketplaces. The guides embedded within the spreadsheet framework help users understand sizing conversions, shipping timelines, quality indicators and common pitfalls to avoid. This combination of <strong className="text-foreground-900">curated data and buyer education</strong> transforms the shopping experience from overwhelming to empowering.
              </p>

              <p>
                Whether you are building a seasonal wardrobe refresh, hunting for gift ideas or simply curious about what is trending in global fashion markets, the Kakobuy Spreadsheet provides a structured starting point. With categories spanning from Shoes and Hoodies to Perfumes and Accessories, the spreadsheet format ensures that no matter your style preference or budget, there is an organized path to discovering products you will genuinely love.
              </p>
            </div>

            <div className="mt-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-base font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Explore Kakobuy Spreadsheet
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SEOArticlesSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Kakobuy Spreadsheet SEO Guides
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Dive deep into our comprehensive guides covering every aspect of spreadsheet shopping research. Each article is packed with original insights and practical strategies.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          <Link
            to="/kakobuy-shoes-spreadsheet"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Beautiful%20sneakers%20and%20footwear%20product%20photography%20with%20warm%20neutral%20beige%20background%2C%20minimalist%20arrangement%20of%20white%20and%20cream%20colored%20designer%20sneakers%20on%20wooden%20surface%2C%20soft%20natural%20window%20light%2C%20editorial%20product%20shot%20style%2C%20clean%20aesthetic%20with%20negative%20space%2C%20fashion%20e-commerce%20catalog%20photography&width=800&height=600&seq=article-card-shoes&orientation=landscape"
                alt="Kakobuy Shoes Spreadsheet Guide"
                title="Kakobuy Shoes Spreadsheet Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Shoes Spreadsheet Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Discover trending sneakers, footwear finds and shoe shopping strategies organized by category.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-hoodies-spreadsheet"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20knitwear%20and%20hoodies%20flat%20lay%20on%20cream%20linen%20texture%20background%2C%20beige%20cream%20and%20warm%20taupe%20colored%20sweaters%20neatly%20folded%2C%20soft%20diffused%20natural%20light%2C%20minimalist%20fashion%20photography%2C%20organic%20warm%20tones%2C%20editorial%20catalog%20style%2C%20peaceful%20serene%20composition&width=800&height=600&seq=article-card-hoodies&orientation=landscape"
                alt="Kakobuy Hoodies and Sweaters Guide"
                title="Kakobuy Hoodies and Sweaters Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Hoodies &amp; Sweaters Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Seasonal hoodie and sweater discoveries, fabric quality tips and sizing recommendations.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-tshirts-spreadsheet"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Premium%20cotton%20t-shirts%20arranged%20neatly%20on%20light%20beige%20surface%2C%20soft%20pastel%20and%20earth%20tone%20colors%2C%20clean%20minimalist%20product%20photography%2C%20gentle%20natural%20window%20lighting%2C%20editorial%20flat%20lay%20style%2C%20fabric%20texture%20visible%2C%20fashion%20e-commerce%20aesthetic%2C%20warm%20neutral%20background&width=800&height=600&seq=article-card-tshirts&orientation=landscape"
                alt="Kakobuy T-Shirts Spreadsheet Guide"
                title="Kakobuy T-Shirts Spreadsheet Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy T-Shirts Spreadsheet Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Graphic tees, everyday basics and unique prints curated for style and comfort.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-jackets-spreadsheet"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Stylish%20jackets%20and%20outerwear%20displayed%20on%20wooden%20hangers%20against%20cream%20wall%20background%2C%20beige%20trench%20coat%20and%20warm%20toned%20bomber%20jacket%2C%20soft%20studio%20lighting%2C%20editorial%20fashion%20photography%2C%20minimalist%20boutique%20display%20style%2C%20elegant%20composition%2C%20warm%20neutral%20palette&width=800&height=600&seq=article-card-jackets&orientation=landscape"
                alt="Kakobuy Jackets Spreadsheet Guide"
                title="Kakobuy Jackets Spreadsheet Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Jackets Spreadsheet Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Outerwear essentials from lightweight windbreakers to premium winter coats.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-accessories-spreadsheet"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20accessories%20flat%20lay%20with%20leather%20handbags%20watches%20bracelets%20and%20sunglasses%20arranged%20on%20marble%20surface%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20golden%20light%20reflections%2C%20luxury%20product%20photography%20style%2C%20sophisticated%20editorial%20composition%2C%20neutral%20warm%20background&width=800&height=600&seq=article-card-accessories&orientation=landscape"
                alt="Kakobuy Accessories Spreadsheet Guide"
                title="Kakobuy Accessories Spreadsheet Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Accessories Spreadsheet Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Bags, belts, watches and jewelry finds that complete any outfit perfectly.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-fashion-spreadsheet"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Curated%20fashion%20pieces%20including%20clothing%20accessories%20and%20footwear%20artistically%20arranged%20on%20light%20linen%20background%2C%20warm%20terracotta%20and%20cream%20color%20harmony%2C%20soft%20natural%20morning%20light%2C%20editorial%20lifestyle%20photography%2C%20organic%20flowing%20composition%2C%20sophisticated%20warm%20aesthetic&width=800&height=600&seq=article-card-fashion&orientation=landscape"
                alt="Kakobuy Fashion Spreadsheet Guide"
                title="Kakobuy Fashion Spreadsheet Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Fashion Spreadsheet Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Complete fashion discovery across all categories with styling inspiration.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-shopping-tips"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Organized%20creative%20workspace%20with%20laptop%20notebook%20and%20fashion%20magazine%20on%20wooden%20desk%2C%20warm%20morning%20light%20from%20window%2C%20cream%20and%20beige%20color%20palette%2C%20cozy%20productive%20atmosphere%2C%20lifestyle%20photography%2C%20minimalist%20desk%20setup%2C%20peaceful%20organized%20creative%20space&width=800&height=600&seq=article-card-tips&orientation=landscape"
                alt="Kakobuy Shopping Tips and Strategies"
                title="Kakobuy Shopping Tips and Strategies"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Shopping Tips &amp; Strategies
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Expert tips for smarter spreadsheet shopping, saving money and finding hidden gems.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-spreadsheet-comparison"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Side%20by%20side%20comparison%20concept%20with%20two%20neatly%20arranged%20product%20collections%20on%20split%20surface%2C%20warm%20beige%20tones%20with%20subtle%20contrast%2C%20organized%20fashion%20items%20displayed%20symmetrically%2C%20soft%20studio%20lighting%2C%20clean%20editorial%20photography%2C%20balanced%20harmonious%20composition&width=800&height=600&seq=article-card-compare&orientation=landscape"
                alt="Kakobuy Spreadsheet Comparison Guide"
                title="Kakobuy Spreadsheet Comparison Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Spreadsheet Comparison
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Compare Kakobuy with other spreadsheet platforms and discover the advantages.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>

          <Link
            to="/kakobuy-safety-guide"
            className="group bg-background-50 border border-background-200/80 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300/60 cursor-pointer"
          >
            <div className="w-full h-48 md:h-52 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Secure%20shopping%20concept%20with%20padlock%20and%20shopping%20bag%20on%20clean%20cream%20surface%2C%20warm%20natural%20light%2C%20minimalist%20composition%2C%20trust%20and%20security%20visual%20metaphor%2C%20soft%20golden%20tones%2C%20clean%20elegant%20product%20photography%2C%20peaceful%20protective%20atmosphere&width=800&height=600&seq=article-card-safety&orientation=landscape"
                alt="Kakobuy Shopping Safety Guide"
                title="Kakobuy Shopping Safety Guide"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="p-5">
              <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                Kakobuy Shopping Safety Guide
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Essential safety tips, scam prevention strategies and buyer protection advice.
              </p>
              <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-primary-500">
                Read Article <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

function ArticleCTASection() {
  return (
    <section className="py-16 md:py-20 bg-secondary-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
        <div className="bg-background-50 rounded-3xl border border-background-200/70 p-8 md:p-12 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]">
            <img
              src="https://readdy.ai/api/search-image?query=Soft%20abstract%20warm%20toned%20gradient%20with%20subtle%20textile%20texture%20pattern%2C%20cream%20beige%20and%20warm%20terracotta%20watercolor%20wash%2C%20minimal%20abstract%20art%20background%2C%20gentle%20organic%20flowing%20shapes%2C%20very%20light%20pastel%20tones%2C%20calm%20peaceful%20aesthetic%2C%20barely%20visible%20pattern%20suitable%20for%20text%20overlay&width=1200&height=800&seq=kakobuy-cta-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-accent-100 flex items-center justify-center">
              <i className="ri-article-line text-2xl text-accent-600"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Kakobuy Spreadsheet: The Complete Guide
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mb-8 max-w-lg mx-auto">
              Dive deeper into how the Kakobuy Spreadsheet works, learn advanced research strategies and discover hidden finds across every product category.
            </p>
            <Link
              to="/kakobuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-full text-base font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Read Full Guide
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}